import { stringToUint8Array } from "uint8array-extras";
import { setSourceAccount } from "../util/operations.js";
import {
  DataValue,
  ManageDataOp,
  MuxedAccount,
  Operation,
  OperationBody,
} from "../../xdr/index.js";
import { ManageDataOpts, OperationAttributes } from "./types.js";

/**
 * This operation adds data entry to the ledger.
 * @param opts - Options object
 *   - `name`: The name of the data entry.
 *   - `value`: The value of the data entry.
 *   - `source`: The optional source account.
 */
export function manageData(opts: ManageDataOpts): Operation {
  if (!(typeof opts.name === "string" && opts.name.length <= 64)) {
    throw new Error("name must be a string, up to 64 characters");
  }

  // undefined is accepted (treated as null/delete) for internal round-trip
  // compatibility: fromXdrObject returns undefined for absent optional fields.
  // The public API contract is null for delete-entry.
  if (
    typeof opts.value !== "string" &&
    !(opts.value instanceof Uint8Array) &&
    opts.value !== null &&
    opts.value !== undefined
  ) {
    throw new Error("value must be a string, Uint8Array or null");
  }

  let dataValue: Uint8Array | null;
  if (typeof opts.value === "string") {
    dataValue = stringToUint8Array(opts.value);
  } else {
    dataValue = opts.value ?? null;
  }

  if (dataValue !== null && dataValue.length > 64) {
    throw new Error("value cannot be longer that 64 bytes");
  }

  const manageDataOp = new ManageDataOp({
    dataName: opts.name,
    dataValue:
      dataValue === null ? null : new DataValue(Uint8Array.from(dataValue)),
  });

  const opAttributes: OperationAttributes = {
    sourceAccount: null,
    body: OperationBody.manageData(manageDataOp),
  };
  setSourceAccount(opAttributes, opts);

  return new Operation(
    opAttributes as {
      sourceAccount: MuxedAccount | null;
      body: OperationBody;
    },
  );
}
